import { HttpEventType } from '@angular/common/http';
import { booleanAttribute, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { defaultIfEmpty, Observable, of, Subject, takeUntil } from 'rxjs';

import { SnackbarService } from "../snackbar/snackbar.service";

import { UploadLargeFileService, UploadStatus } from './upload-large-file.service';

export interface UploadProgress {
  progress: number;
  status: UploadStatus;
  stage?: 'uploading' | 'processing' | 'finalizing'; // Current upload stage
  statusMessage?: string; // Human-readable status message
  error?: string;
  fileName: string;
  uploadId?: string;
  uploadSpeed?: number; // bytes per second
  eta?: number; // estimated time remaining in seconds
  loaded?: number; // bytes uploaded
  total?: number; // total bytes
}

@Component({
  selector: 'app-upload-large-file',
  templateUrl: './upload-large-file.component.html',
  styleUrls: [ './upload-large-file.component.scss' ]
})
export class UploadLargeFileComponent implements OnInit, OnDestroy {

  fileName?: string;

  acceptingMimeTypes!: string;

  uploadProgress: UploadProgress | null = null;

  isDragOver = false;

  uploadId?: string;

  // New state to track if file is selected but not yet uploading
  fileReady = false;

  private destroy$ = new Subject<void>();

  @Input() mimeTypes$?: Observable<string>;

  @Input({ transform: booleanAttribute }) image = false;

  @Input({ transform: booleanAttribute }) video = false;

  @Input({ transform: booleanAttribute }) audio = false;

  @Input({ transform: booleanAttribute }) pdf = false;

  @Input({ transform: booleanAttribute }) update = false;

  @Input({ transform: booleanAttribute }) hidden = false;

  @Input({ transform: booleanAttribute }) outline = false;

  @Input({ transform: booleanAttribute }) link = false;

  @Input({ transform: booleanAttribute }) showFileName = false;

  @Input({ transform: booleanAttribute }) showMessage = true;

  @Input({ transform: booleanAttribute }) showImageUrl = false;

  @Input() imageUrl = '';

  @Input() entityName: 'contents' | 'thematics' | 'my-profile' | 'thematics-details' | 'formation-contents' = 'contents';

  @Input() entityId = '';

  @Input() isCover = false;

  @Output() updateFile = new EventEmitter<File>();

  @Output() uploadComplete = new EventEmitter<{ fileName: string; uploadId: string }>();

  @Output() uploadError = new EventEmitter<string>();

  @Output() uploadProgress$ = new EventEmitter<UploadProgress>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private readonly snackBarService: SnackbarService,
    private cdr: ChangeDetectorRef,
    private uploadService: UploadLargeFileService
  ) {}

  ngOnInit(): void {
    if (!this.mimeTypes$) {
      this.mimeTypes$ = of('csv');
    } else {
      this.mimeTypes$ = this.mimeTypes$.pipe(
        defaultIfEmpty('csv')
      );
    }

    this.mimeTypes$.pipe(takeUntil(this.destroy$)).subscribe(mimeTypes => {
      this.acceptingMimeTypes = mimeTypes;
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get message() {
    return (this.image && !this.link ?
      (this.update ? "Modifier l'image" : 'Ajouter une image')
      : "");
  }

  get isUploading(): boolean {
    return this.uploadProgress?.status === 'uploading';
  }

  get canPause(): boolean {
    return this.isUploading && !!this.uploadId;
  }

  get canResume(): boolean {
    return this.uploadProgress?.status === 'paused' && !!this.uploadId;
  }

  get canRetry(): boolean {
    return this.uploadProgress?.status === 'error';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k**i).toFixed(2))  } ${  sizes[i]}`;
  }

  formatSpeed(bytesPerSecond: number): string {
    if (!bytesPerSecond || bytesPerSecond === 0) return '0 KB/s';
    const k = 1024;
    const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
    return `${parseFloat((bytesPerSecond / k**i).toFixed(1))} ${sizes[i]}`;
  }

  formatETA(seconds: number): string {
    if (!seconds || seconds <= 0 || !isFinite(seconds)) return 'Calcul...';

    if (seconds < 60) {
      return `${Math.ceil(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const secs = Math.ceil(seconds % 60);
      return `${minutes}m ${secs}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }

  onFileSelected(event: EventTarget | null) {
    const file = (event as HTMLInputElement).files?.[0];
    if (file) {
      this.handleFileUpload(file);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileUpload(files[0]);
    }
  }

  private handleFileUpload(file: File) {
    this.fileName = file.name;
    this.updateFile.emit(file);

    // Validate file type first
    if (!this.validateFileType(file)) {
      this.fileReady = false;
      this.uploadProgress = {
        progress: 0,
        status: 'error',
        fileName: file.name,
        error: 'Type de fichier non supporté'
      };
      this.uploadError.emit('Type de fichier non supporté');
      return;
    }

    // For new content creation (no entityId), just mark file as ready
    // Don't show progress bar until actual upload starts
    if ((this.entityName === 'contents' || this.entityName === 'formation-contents') && (!this.entityId || this.entityId.trim().length === 0)) {
      this.fileReady = true;
      this.uploadProgress = null; // No progress bar yet
      return;
    }

    // For existing content, start upload immediately
    this.fileReady = false;
    this.startUpload(file);
  }

  private validateFileType(file: File): boolean {
    if (!this.acceptingMimeTypes) return true;

    const allowedExtensions = this.acceptingMimeTypes.split(',').map(ext => ext.trim());
    const fileExtension = `.${  file.name.split('.').pop()?.toLowerCase()}`;

    return allowedExtensions.includes(fileExtension);
  }

  private startUpload(file: File) {
    // Initialize upload progress when upload actually starts
    this.fileReady = false;
    this.uploadProgress = {
      progress: 0,
      status: 'uploading',
      fileName: file.name,
      total: file.size,
      loaded: 0,
      stage: 'uploading',
      statusMessage: 'Préparation...'
    };

    this.uploadService.uploadLargeFile(file, this.entityName, this.entityId, this.isCover)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            // Use scaledProgress from service (0-85% range)
            const scaledProgress = (event as any).scaledProgress;
            this.uploadProgress!.progress = scaledProgress !== undefined ? scaledProgress : Math.round(100 * event.loaded / event.total);
            this.uploadProgress!.loaded = event.loaded;
            this.uploadProgress!.total = event.total;
            this.uploadProgress!.stage = (event as any).stage || 'uploading';
            this.uploadProgress!.statusMessage = (event as any).statusMessage || 'Envoi du fichier...';

            // Extract speed and ETA from event (added by service)
            if ((event as any).uploadSpeed) {
              this.uploadProgress!.uploadSpeed = (event as any).uploadSpeed;
            }
            if ((event as any).eta) {
              this.uploadProgress!.eta = (event as any).eta;
            }

            this.uploadProgress$.emit(this.uploadProgress!);
          } else if (event.type === HttpEventType.Response) {
            // Show processing stage briefly before completion
            this.uploadProgress!.status = 'processing';
            this.uploadProgress!.progress = 95;
            this.uploadProgress!.stage = 'processing';
            this.uploadProgress!.statusMessage = 'Finalisation...';
            this.cdr.detectChanges();

            // Complete after brief delay to show processing stage
            setTimeout(() => {
              this.uploadProgress!.status = 'completed';
              this.uploadProgress!.progress = 100;
              this.uploadProgress!.stage = 'finalizing';
              this.uploadProgress!.statusMessage = 'Terminé!';
              this.uploadComplete.emit({
                fileName: this.fileName!,
                uploadId: this.uploadId || ''
              });
              this.snackBarService.pushMessage('Fichier uploadé avec succès', 'success');
              this.cdr.detectChanges();
            }, 500);
          }
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.uploadProgress!.status = 'error';
          this.uploadProgress!.error = this.getErrorMessage(error);
          this.uploadError.emit(this.uploadProgress!.error);
          this.snackBarService.pushMessage(this.uploadProgress!.error, 'error');
          this.cdr.detectChanges();
        }
      });
  }

  pauseUpload() {
    if (this.uploadId) {
      this.uploadService.pauseUpload(this.uploadId);
      this.uploadProgress!.status = 'paused';
      this.cdr.detectChanges();
    }
  }

  resumeUpload() {
    if (this.uploadId && this.uploadProgress) {
      this.uploadService.resumeUpload(this.uploadId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress && event.total) {
              this.uploadProgress!.progress = Math.round(100 * event.loaded / event.total);
              this.uploadProgress!.status = 'uploading';
              this.uploadProgress$.emit(this.uploadProgress!);
            } else if (event.type === HttpEventType.Response) {
              this.uploadProgress!.status = 'completed';
              this.uploadProgress!.progress = 100;
              this.uploadComplete.emit({
                fileName: this.fileName!,
                uploadId: this.uploadId || ''
              });
            }
            this.cdr.detectChanges();
          },
          error: (error) => {
            this.uploadProgress!.status = 'error';
            this.uploadProgress!.error = this.getErrorMessage(error);
            this.uploadError.emit(this.uploadProgress!.error);
            this.cdr.detectChanges();
          }
        });
    }
  }

  retryUpload() {
    if (this.fileName) {
      // Reset progress and retry
      this.uploadProgress = {
        progress: 0,
        status: 'preparing',
        fileName: this.fileName
      };

      // Get the file from the input again or trigger file selection
      const file = this.fileInput.nativeElement.files?.[0];
      if (file) {
        this.startUpload(file);
      } else {
        this.click();
      }
    }
  }

  cancelUpload() {
    if (this.uploadId) {
      this.uploadService.cancelUpload(this.uploadId);
    }
    this.uploadProgress = null;
    this.uploadId = undefined;
    this.fileName = undefined;
    this.fileReady = false;
    this.fileInput.nativeElement.value = '';
    this.cdr.detectChanges();
  }

  private getErrorMessage(error: any): string {
    if (error?.error?.message) {
      return error.error.message;
    }
    if (error?.message) {
      return error.message;
    }
    if (error?.status === 413) {
      return 'Fichier trop volumineux';
    }
    if (error?.status === 408 || error?.name === 'TimeoutError') {
      return 'Timeout - Veuillez réessayer';
    }
    if (error?.status === 0) {
      return 'Erreur de connexion - Vérifiez votre réseau';
    }
    return 'Erreur lors de l\'upload du fichier';
  }

  click() {
    this.fileInput.nativeElement.click();
  }

  // Method to trigger upload after content creation
  uploadFileToEntity(entityId: string) {
    if (!this.fileName || !entityId || !this.fileReady) {
      return;
    }

    // Update entity ID
    this.entityId = entityId;

    // Get the file from input
    const file = this.fileInput.nativeElement.files?.[0];
    if (file) {
      this.startUpload(file);
    }
  }
}